import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, CheckCircle2, HardHat, Wrench } from "lucide-react";
import { usePortalAuth } from "@/components/portal/PortalAuth";
import { ScheduleInstallModal } from "@/components/portal/QuickActions";
import { Card, EmptyState, Spinner, StatusBadge, fmtDay, simpleOrderStatus } from "@/components/portal/shared";

const DONE = ["Installed", "Balance Paid", "Completed"];

export default function InstallationsPage() {
  const { api } = usePortalAuth();
  const [orders, setOrders] = useState(null);
  const [sched, setSched] = useState(null);

  const load = () => api.get("/orders").then((r) => setOrders(r.data.orders)).catch(() => setOrders([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  if (orders === null) return <Spinner />;

  const today = new Date().toISOString().slice(0, 10);
  const upcoming = orders.filter((o) => o.install_date && o.install_date >= today && !DONE.includes(o.status));
  const unscheduled = orders.filter((o) => !o.install_date && ["Order Confirmed", "Manufacturing", "Deposit Paid"].includes(o.status));
  const completed = orders.filter((o) => DONE.includes(o.status));

  const markInstalled = async (o) => {
    await api.patch(`/orders/${o.id}`, { status: "Installed" });
    await load();
  };

  const JobCard = ({ o, muted }) => (
    <li className={`rounded-2xl border border-warm-200 bg-white p-5 shadow-sm ${muted ? "opacity-75" : ""}`} data-testid={`install-row-${o.id}`}>
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
        <span className="min-w-40">
          <span className="block text-sm font-semibold text-navy-900">{o.ref}</span>
          <Link to={`/portal/customers/${o.lead_id}?tab=installations`} className="block text-xs text-navy-900/70 hover:underline">{o.customer}</Link>
        </span>
        <span className="text-xs text-slate-600 flex items-center gap-1.5">
          <CalendarDays className="h-3.5 w-3.5" />
          {o.install_date ? `${fmtDay(o.install_date)}${o.install_time ? ` · ${o.install_time}` : ""}` : "Not scheduled"}
        </span>
        <span className="text-xs text-slate-600">{o.installer || "No installer"}</span>
        <span className="text-xs text-slate-500 hidden md:block">{[o.address1, o.town, o.postcode].filter(Boolean).join(", ")}</span>
        <span className="ml-auto"><StatusBadge s={simpleOrderStatus(o.status)} /></span>
      </div>
      {o.completion_signature && (
        <p className="mt-3 rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-2.5 text-xs text-emerald-900 flex items-center gap-2">
          <CheckCircle2 className="h-3.5 w-3.5" /> Signed by {o.completion_name}{o.child_safety_advised ? " · child-safety advised" : ""}
        </p>
      )}
      {!DONE.includes(o.status) && (
        <div className="flex flex-wrap gap-2 mt-3">
          <button type="button" data-testid={`install-schedule-${o.id}`} onClick={() => setSched(o)} className="inline-flex items-center gap-1 rounded-full bg-navy-900 px-3.5 py-1.5 text-[11px] font-semibold text-white hover:bg-navy-800">
            <Wrench className="h-3 w-3" /> {o.install_date ? "Reschedule" : "Schedule"}
          </button>
          {o.install_date && (
            <button type="button" data-testid={`install-complete-${o.id}`} onClick={() => markInstalled(o)} className="inline-flex items-center gap-1 rounded-full border border-warm-300 px-3.5 py-1.5 text-[11px] font-semibold text-slate-600 hover:border-navy-800/40">
              <CheckCircle2 className="h-3 w-3" /> Mark Installed
            </button>
          )}
        </div>
      )}
    </li>
  );

  return (
    <div data-testid="installations-page" className="space-y-5">
      <p className="text-sm text-slate-500">{upcoming.length} upcoming · {unscheduled.length} awaiting scheduling · {completed.length} completed</p>
      {orders.length === 0 ? (
        <EmptyState icon={HardHat}>No installations yet. Orders appear here once confirmed.</EmptyState>
      ) : (
        <>
          <Card title="Upcoming Installations" testid="installs-upcoming">
            {upcoming.length === 0 ? <p className="text-sm text-slate-400">Nothing scheduled.</p> : <ul className="space-y-3">{upcoming.map((o) => <JobCard key={o.id} o={o} />)}</ul>}
          </Card>
          {unscheduled.length > 0 && (
            <Card title="Awaiting Scheduling" testid="installs-unscheduled">
              <ul className="space-y-3">{unscheduled.map((o) => <JobCard key={o.id} o={o} />)}</ul>
            </Card>
          )}
          {completed.length > 0 && (
            <Card title="Completed" testid="installs-completed">
              <ul className="space-y-3">{completed.map((o) => <JobCard key={o.id} o={o} muted />)}</ul>
            </Card>
          )}
        </>
      )}
      {sched && <ScheduleInstallModal order={sched} onClose={() => setSched(null)} onDone={load} />}
    </div>
  );
}
